import { CheckCircleIcon, QuestionIcon, RocketLaunchIcon, WarningOctagonIcon } from '@phosphor-icons/react'
import { match } from 'ts-pattern'
import { pluralize } from '../../utils/text-utils.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { EmptyCompositionBar } from '../composition-bar/empty-composition-bar.tsx'
import { SegmentsBar, type SegmentProps } from '../composition-bar/segments-bar.tsx'
import { PulsingDot } from '../pulsing-dot.tsx'
import { TooltipWithHover } from '../tooltip.tsx'
import { HiddenByScopeNote } from './hidden-by-scope-note.tsx'
import { aggregateBuckets, toBucketFilter, type Categorization } from './resolution-categorization.ts'
import * as styles from './resolution-collapsed-bar.css.ts'
import {
  areFiltersEqual,
  getActionableTotal,
  getAnalyzedTotal,
  getNextAction,
  getResolvedTotal,
  RESOLUTION_META,
  RESOLUTION_STATES,
  toPercentage,
  toStateFilter,
  type ResolutionFilter,
  type ResolutionPanelData,
  type ResolutionState,
} from './resolution-taxonomy.ts'

export type ResolutionCollapsedBarProps = {
  data: ResolutionPanelData
  activeFilter?: ResolutionFilter
  onFilterChange?: (filter: ResolutionFilter | undefined) => unknown
  /**
   * Filters the table to the next action's findings. The design also calls for preselecting those
   * rows so the bulk action bar appears in one click; that half arrives with the bulk-action work
   * in ISS-8774, so today this behaves like `onFilterChange` unless a caller passes something richer.
   */
  onApplyNextAction?: (filter: ResolutionFilter) => unknown
  /** Findings removed from the data set by the active data scope, footnoted beside the resolved count. */
  hiddenByScopeCount?: number
  /**
   * The analysed share of `hiddenByScopeCount`. The resolved count's denominator adds this rather
   * than `hiddenByScopeCount`, because it counts analysed findings only.
   */
  hiddenAnalyzedByScopeCount?: number
  /** The scan's analysis was cancelled: the empty state says so rather than "not initiated". */
  isAnalysisCancelled?: boolean
  /**
   * Whether the table is showing the whole quick view. The analyzing/unanalyzed counters and the
   * next-action CTA describe findings outside a narrowed selection, so both are hidden once a
   * filter is applied.
   */
  isUnfiltered?: boolean
  /**
   * Whether the quick view is All findings. The analyzing and unanalyzed counters count findings
   * across the whole scan, and only this view includes them, so any narrower view hides both.
   */
  isAllFindingsView?: boolean
  /**
   * How the resolution states group into the segments this bar draws. Without one the bar draws a
   * segment per state, which is what the Pixee-default categorization produces anyway.
   */
  categorization?: Categorization
}

/**
 * The collapsed state of the resolution panel: the single most important indicator —
 * how many analyzed findings still need action — plus what to do next and a thin
 * composition strip of the whole resolution mix.
 */
export const ResolutionCollapsedBar: React.FC<ResolutionCollapsedBarProps> = ({
  data,
  activeFilter,
  onFilterChange,
  onApplyNextAction,
  hiddenByScopeCount = 0,
  hiddenAnalyzedByScopeCount = 0,
  isAnalysisCancelled = false,
  isUnfiltered = true,
  isAllFindingsView = true,
  categorization,
}) => {
  const { resolution, inProgressCount, unanalyzedCount } = data
  const analyzedTotal = getAnalyzedTotal(resolution)

  if (analyzedTotal === 0) {
    return (
      <UnanalyzedEmptyState
        unanalyzedCount={unanalyzedCount}
        inProgressCount={inProgressCount}
        isAnalysisCancelled={isAnalysisCancelled}
      />
    )
  }

  const actionableTotal = getActionableTotal(resolution)
  const resolvedTotal = getResolvedTotal(resolution)
  // Measured against the original data set: hidden analysed findings stay in the denominator so the
  // counter describes the whole scan rather than the scoped subset. It lowers the percentage rather
  // than flattering it — hidden findings are not assumed resolved.
  const originalAnalyzedTotal = analyzedTotal + hiddenAnalyzedByScopeCount
  const resolvedPercentage = Math.round(toPercentage({ count: resolvedTotal, total: originalAnalyzedTotal }))
  const nextAction = getNextAction(resolution)
  const hasUnanalyzedCounters = isAllFindingsView && isUnfiltered

  // One segment per bucket when a categorization is set, otherwise one per state. The tooltip reads
  // count-first either way — a bucket's own name lowercased stands in for the state's `tipLabel`,
  // since a custom bucket has no grammar of its own to draw on.
  const segmentSources: { key: string; count: number; label: string; color: string; filter: ResolutionFilter }[] =
    categorization
      ? aggregateBuckets({ categorization, resolution }).map(bucket => ({
          key: bucket.id,
          count: bucket.count,
          label: bucket.name.toLowerCase(),
          color: bucket.color,
          filter: toBucketFilter(bucket),
        }))
      : RESOLUTION_STATES.map(state => ({
          key: state,
          count: resolution[state],
          label: RESOLUTION_META[state].tipLabel,
          color: RESOLUTION_META[state].color,
          filter: toStateFilter(state),
        }))

  const segments: SegmentProps[] = segmentSources
    .filter(source => source.count > 0)
    .map(source => {
      const isActive = areFiltersEqual(activeFilter, source.filter)
      const summary = `${source.count} ${source.label}`
      return {
        ariaLabel: summary,
        tooltip: summary,
        percentage: toPercentage({ count: source.count, total: analyzedTotal }),
        color: source.color,
        pressed: isActive,
        dimmed: !!activeFilter && !isActive,
        onClick: onFilterChange ? () => onFilterChange(isActive ? undefined : source.filter) : undefined,
      }
    })

  return (
    <div className={styles.container}>
      <div className={styles.headline}>
        <span className={styles.headlineCount}>{actionableTotal}</span>
        <span className={styles.headlineLabel}>
          {pluralize(actionableTotal, 'analyzed finding needs', 'analyzed findings need')} action
        </span>
        {nextAction && isUnfiltered && (
          <DefaultButton
            buttonType="secondary"
            size="small"
            className={styles.nextAction}
            onClick={() => (onApplyNextAction ?? onFilterChange)?.(nextAction.filter)}
          >
            {/* DefaultButton only renders its `icon` prop for destructive buttons, so the
                icon rides along in the children. */}
            <NextActionIcon state={nextAction.state} />
            <strong>{nextAction.count}</strong> {nextAction.label}
          </DefaultButton>
        )}
      </div>
      <div className={styles.metaRow}>
        <span className={styles.resolved}>
          <CheckCircleIcon className={styles.resolvedIcon} size={14} weight="fill" />
          {resolvedTotal} of {originalAnalyzedTotal} resolved{' '}
          <span className={styles.resolvedPercentage}>({resolvedPercentage}%)</span>
          <HiddenByScopeNote count={hiddenByScopeCount} />
        </span>
        {hasUnanalyzedCounters && inProgressCount > 0 && (
          <TooltipWithHover
            trigger={
              <span className={styles.secondaryCounter}>
                <PulsingDot variant="in-progress" size={16} />
                <span className={styles.inProgressCount}>{inProgressCount}</span> analyzing
              </span>
            }
            side="top"
          >
            Findings still being analyzed — not yet included in the count above
          </TooltipWithHover>
        )}
        {hasUnanalyzedCounters && unanalyzedCount > 0 && (
          <TooltipWithHover
            trigger={
              <span className={styles.secondaryCounter}>
                <span className={styles.unanalyzedCount}>{unanalyzedCount}</span> unanalyzed
              </span>
            }
            side="top"
          >
            Findings not yet analyzed — not included in the count above
          </TooltipWithHover>
        )}
      </div>
      <div className={styles.strip}>
        <SegmentsBar segments={segments} size="thin" />
      </div>
    </div>
  )
}

/**
 * The glyph tracks the action, but the tint is always Pulse in dark theme — the CTA reads as the one
 * brand-accented affordance in the collapsed bar regardless of which action it offers.
 */
const NextActionIcon: React.FC<{ state: ResolutionState }> = ({ state }) =>
  match(state)
    .with('fixReady', () => <RocketLaunchIcon size={18} weight="fill" className={styles.nextActionIcon} />)
    .with('untriaged', () => <QuestionIcon size={18} weight="fill" className={styles.nextActionIcon} />)
    .otherwise(() => <WarningOctagonIcon size={18} weight="fill" className={styles.nextActionIcon} />)

/**
 * Shown when nothing has finished analysis yet. Counts in-progress findings alongside the untouched
 * ones, so a scan mid-analysis is not described as never started.
 */
const UnanalyzedEmptyState: React.FC<{
  unanalyzedCount: number
  inProgressCount: number
  isAnalysisCancelled: boolean
}> = ({ unanalyzedCount, inProgressCount, isAnalysisCancelled }) => {
  const awaitingCount = unanalyzedCount + inProgressCount

  return (
    <div className={styles.container}>
      <div className={styles.headline}>
        <span className={styles.headlineCount}>{awaitingCount}</span>
        <span className={styles.headlineLabel}>
          {pluralize(awaitingCount, 'finding', 'findings')} awaiting analysis
        </span>
      </div>
      <div className={styles.metaRow}>
        {inProgressCount > 0 ? (
          <span className={styles.secondaryCounter}>
            <PulsingDot variant="in-progress" size={16} />
            <span className={styles.inProgressCount}>{inProgressCount}</span> analyzing
          </span>
        ) : (
          <span className={styles.emptyStateText}>
            {isAnalysisCancelled ? 'Analysis was cancelled' : 'Analysis has not been initiated'}
          </span>
        )}
      </div>
      <div className={styles.strip}>
        <EmptyCompositionBar ariaLabel="No analyzed findings" size="thin" />
      </div>
    </div>
  )
}
